import '@mui/icons-material/PictureAsPdf';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import { Box, Button, Paper, Typography } from '@mui/material';
import PdfPreview from 'components/@home/modals/PdfPreview';
import DropzoneBox from 'components/common/DropzoneBox';
import { bool, func, object, oneOf, string } from 'prop-types';
import React from 'react';
import { I18n } from 'utils/i18n';

const StepPdfSource = ({
  template,
  uploadBusy = false,
  onPdfDrop,
  canvasPdfSrc = null,
  onReplacePdf,
  suppressPreview = false,
  copyVariant = 'editor',
  variant = 'default',
}) => {
  const attachmentKeyForUpload =
    template.documentSource === 'fixed' ? 'templateAttachment' : 'layoutSampleAttachment';
  const previewFileName = `${(template.name || 'document').trim() || 'document'}.pdf`;

  const useSendSessionCopy = copyVariant === 'sendSession' && template.documentSource !== 'fixed';

  if (variant === 'compactBar') {
    if (!canvasPdfSrc) return null;
    return (
      <Button
        color="secondary"
        disabled={uploadBusy}
        onClick={onReplacePdf}
        size="small"
        startIcon={<CloudUploadOutlinedIcon color="secondary" />}
        variant="outlined"
        sx={{ my: -1 }}
      >
        {I18n.t('HrBot.EditTemplate.TemplatePdf.replacePdf')}
      </Button>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 1 }}>
        {useSendSessionCopy
          ? I18n.t('HrBot.EditTemplate.TemplatePdf.sendSessionTitle')
          : I18n.t('HrBot.EditTemplate.TemplatePdf.title')}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
        }}
      >
        {template.documentSource === 'fixed'
          ? I18n.t('HrBot.EditTemplate.TemplatePdf.uploadFixed')
          : useSendSessionCopy
            ? I18n.t('HrBot.EditTemplate.TemplatePdf.sendSessionUploadLayout')
            : I18n.t('HrBot.EditTemplate.TemplatePdf.uploadLayoutSample')}
      </Typography>
      <DropzoneBox
        onDrop={files => onPdfDrop(files, attachmentKeyForUpload)}
        loading={uploadBusy}
        multiple
        sx={{ minHeight: canvasPdfSrc ? 96 : 210 }}
        hint={I18n.t('HrBot.EditTemplate.TemplatePdf.dropHint')}
        accept="application/pdf"
      />
      {!!canvasPdfSrc && (
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Box
            sx={{
              alignItems: 'center',
              borderBottom: suppressPreview ? 0 : 1,
              borderColor: 'divider',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              justifyContent: 'space-between',
              px: 2,
              py: 1.25,
            }}
          >
            <Typography
              variant="body2"
              noWrap
              sx={{
                color: 'text.secondary',
                minWidth: 0,
                flex: 1,
              }}
            >
              {previewFileName}
            </Typography>
            <Button size="small" variant="outlined" color="secondary" onClick={onReplacePdf}>
              {I18n.t('HrBot.EditTemplate.TemplatePdf.replacePdf')}
            </Button>
          </Box>
          {!suppressPreview && (
            <Box sx={{ maxHeight: 420, overflow: 'auto' }}>
              <PdfPreview src={canvasPdfSrc} fullWidth pageNum={1} showSignature={false} />
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

StepPdfSource.propTypes = {
  template: object.isRequired,
  uploadBusy: bool,
  onPdfDrop: func.isRequired,
  canvasPdfSrc: string,
  onReplacePdf: func.isRequired,
  suppressPreview: bool,
  copyVariant: oneOf(['editor', 'sendSession']),
  variant: oneOf(['default', 'compactBar']),
};

export default StepPdfSource;
